import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { exchangeSession } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

/**
 * Handles `#session_id=…` returned by Emergent Auth.
 *
 * Per playbook:
 * - Detect the fragment SYNCHRONOUSLY (done by AppRouter before we render).
 * - Set the "processed" flag using `useRef` (not state) and set it
 *   synchronously at the top of the effect — protects against React 18
 *   StrictMode double-invocation.
 * - The /auth/session call MUST come from the backend; we only POST the
 *   session_id and let the backend talk to Emergent.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        await exchangeSession(sessionId);
        await refresh();
        // Clear the fragment so a back-button to this page doesn't re-trigger.
        window.history.replaceState({}, "", "/");
        navigate("/", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Login failed");
      }
    })();
  }, [navigate, refresh]);

  return (
    <main className="max-w-md mx-auto px-6 py-24 text-center" data-testid="auth-callback">
      {error ? (
        <>
          <h2 className="font-serif-display text-3xl text-[#A0522D]">Login failed</h2>
          <p className="mt-3 text-sm text-[#6C706D]">{error}</p>
          <a href="/" className="mt-6 inline-block text-sm underline underline-offset-4">
            ← Go home
          </a>
        </>
      ) : (
        <p className="font-mono text-xs text-[#6C706D] uppercase tracking-widest">
          Signing you in…
        </p>
      )}
    </main>
  );
}
